import { DROP } from 'components/common';
import { getAllChordsFromNotes } from './chordIdentifier';

export interface Chord {
  name: string;
  nameBase: string;
  nameModifier: string;
  event: ThemeEvent;
}

export interface ThemeEvent {
  index: number;
  type: 'beat' | 'note' | 'chord' | 'end';
  start: number;
  duration: number;
  spn: string[];
  track?: any;
}

export const getChords = (theme: { events: ThemeEvent[] }): Chord[] =>
  theme.events.filter(isChordEvent).map(
    (event: ThemeEvent): Chord => {
      const name = getAllChordsFromNotes(event.spn, null)[0];
      const nameBase = getAllChordsFromNotes(event.spn.slice(0, 3), null)[0];
      const nameModifier = name.substr(nameBase.length);
      return {
        name,
        nameBase,
        nameModifier,
        event,
      };
    }
  );

export const isChordEvent = (event: ThemeEvent) => event.type === 'chord' && event.track == null;

const EB = {
  visual: {
    stop() {
      window.EulerBeatsGenesis.visual.stop();
      window.EulerBeatsEnigma.visual.stop();
      window.EulerBeatsFutura.visual.stop();
    },
    isPlaying() {
      return window.EulerBeatsGenesis.visual.isPlaying
        || window.EulerBeatsEnigma.visual.isPlaying
        || window.EulerBeatsFutura.visual.isPlaying()
        || window.EulerBeatsFutura.visual.isInitializing();
    },
    play(drop: DROP, element, loop=true) {
      if (drop === DROP.genesis) {
        return window.EulerBeatsGenesis.visual.play(element);
      } else if (drop === DROP.enigma) {
        return window.EulerBeatsEnigma.visual.play(element);
      } else if (drop === DROP.futura) {
        return window.EulerBeatsFutura.visual.play(element.id, 0, true, true, loop);
      }
    },
    toggle(drop: DROP, element, loop=true) {
      if (drop === DROP.genesis) {
        window.EulerBeatsGenesis.visual.toggle(element);
      } else if (drop === DROP.enigma) {
        window.EulerBeatsEnigma.visual.toggle(element);
      } else if (drop === DROP.futura) {
        window.EulerBeatsFutura.visual.toggle(element?.id, loop);
      }
    },
  },
  audio: {
    player: null as null | HTMLAudioElement,
    playbackRate: 1,
    currentTimePercentage: 0,
    chords: null as null | Chord[],
    lastChordEventIndex: null as null | number,

    rewind() {
      EB.audio.updateCurrentTimePercentage(0);
      EB.audio.updatePlaybackRate(1);
      EB.audio.lastChordEventIndex = null;
    },
    updatePlaybackRate: (value: number) => {
      EB.audio.playbackRate = value;
      window.EulerBeatsGenesis.audio._playRate = value;
      window.EulerBeatsEnigma.audio._playRate = value;
      if (EB.audio.player) {
        EB.audio.saveCurrentTime();
        EB.audio.player.playbackRate = value;
      }
    },
    updateCurrentTimePercentage: (value: number) => {
      EB.audio.currentTimePercentage = value;
      if (EB.audio.player) {
        const time = (EB.audio.player.duration * value) / 100;
        if (!isNaN(time)) {
          EB.audio.updateCurrentTime(time);
        }
      }
    },
    updateCurrentTime: (value: number) => {
      if (EB.audio.player) {
        EB.audio.player.currentTime = value;
        EB.audio.saveCurrentTime();
      }
    },
    saveCurrentTime: () => {
      if (!EB.audio.player) {
        return;
      }
      window.EulerBeatsGenesis.audio._eventsStart = new Date().valueOf();
      window.EulerBeatsGenesis.audio._eventsElapsed = EB.audio.player.currentTime;
      window.EulerBeatsGenesis.audio._eventsIndex = 0;
      window.EulerBeatsEnigma.audio._eventsStart = new Date().valueOf();
      window.EulerBeatsEnigma.audio._eventsElapsed = EB.audio.player.currentTime;
      window.EulerBeatsEnigma.audio._eventsIndex = 0;
    },
    getPlayerState: () => {
      if (!EB.audio.player || !EB.audio.chords) {
        return;
      }
      const lastChordIndex = EB.audio.chords.findIndex(
        c => c.event.index === EB.audio.lastChordEventIndex
      );
      const lastChord = EB.audio.chords[lastChordIndex];
      const currentTime = EB.audio.player.currentTime;
      const lastChordIsPlaying =
        lastChord &&
        currentTime >= lastChord.event.start - 0.1 &&
        currentTime < lastChord.event.start + lastChord.event.duration + 0.1;
      const lastChordProgress =
        lastChord &&
        Math.max(
          0,
          Math.min(
            100,
            Math.floor(((currentTime - lastChord.event.start) / lastChord.event.duration) * 100)
          )
        );
      return {
        duration: EB.audio.player.duration,
        currentTime,
        currentTimePercentage: (EB.audio.player.currentTime / EB.audio.player.duration) * 100,
        chords: EB.audio.chords,
        lastChordIndex: lastChordIndex === -1 ? undefined : lastChordIndex,
        lastChordIsPlaying,
        lastChordProgress,
      };
    },
    getChordsInfo: () => {
      if (!EB.audio.player || !EB.audio.chords) {
        return;
      }
      return {
        chords: EB.audio.chords,
        lastChord: EB.audio.chords.find(c => c.event.index === EB.audio.lastChordEventIndex),
      };
    },
  },
};

export default EB;
